import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import OnboardingHeader from '../../components/hospitalOnboarding/OnboardingHeader';
import { useHospitalOnboarding } from '../../contexts/HospitalOnboardingContext';
import {
  Mic,
  Volume2,
  ArrowRight,
  ArrowLeft,
  Edit3,
  Check,
  ShieldCheck,
  Truck,
  Building2,
  Plus,
  AlertCircle
} from 'lucide-react';
import {
  parseVoiceAnswer,
  isSpeechRecognitionSupported
} from '../../utils/voiceOnboardingUtils';
import { getAmbulanceTypeAbbreviation } from '../../utils/ambulanceTypeHelper';

// ============================================================================
// STEP METADATA (Fleet: 6 Steps | Hospital: 5 Steps)
// ============================================================================
const FLEET_STEPS = [
  { id: 1, label: 'Fleet owner profile', desc: 'One owner profile connects all your ambulances.' },
  { id: 2, label: 'Capacity & coverage', desc: 'Tell us where your fleet is usually available.' },
  { id: 3, label: 'Rides & clients', desc: 'Share your existing ride patterns and regular clients.' },
  { id: 4, label: 'Rates', desc: 'Share your rates for the selected ambulance type.' },
  { id: 5, label: 'Add ambulances', desc: 'Complete each ambulance profile, then add another if needed.' },
  { id: 6, label: 'Review & submit', desc: 'Review your fleet setup before final activation.' }
];

const HOSPITAL_STEPS = [
  { id: 1, label: 'Hospital profile', desc: 'Tell us about your hospital and its location.' },
  { id: 2, label: 'Facilities & contacts', desc: 'Help us connect with the right emergency medical and administrative leaders.' },
  { id: 3, label: 'Ambulance requirements', desc: 'Share historical volume and operational demand factors.' },
  { id: 4, label: 'Areas & destinations', desc: 'Share the areas and inter-facility routes you serve.' },
  { id: 5, label: 'Review & submit', desc: 'Review all information before submitting your application.' }
];

// Constants matching manual UI exactly
const OWNERSHIP_OPTIONS = ['Owned', 'Leased', 'Attached'];
const OPERATING_STATUS_OPTIONS = [
  'Active & On-Duty',
  'Standby / Reserve',
  'Scheduled Maintenance',
  'Inactive'
];
const INTERCITY_OPTIONS = ['Yes', 'No', 'Emergency Only'];
const SHIFT_OPTIONS = ['Day Shift', 'Night Shift', '24x7', 'Rotational'];
const DRIVER_APP_OPTIONS = ['Yes (Installed & Active)', 'No (Not Installed)', 'Setup Required'];
const MEDICAL_EQUIPMENT_OPTIONS = [
  'Defibrillator',
  'Patient Monitor',
  'Ventilator',
  'Suction',
  'Infusion Pump',
  'Syringe Pump',
  'Oxygen',
  'AED',
  'ECG',
  'Stretcher',
  'Wheelchair'
];
const TECHNOLOGY_OPTIONS = [
  'GPS',
  'Dashcam',
  'Cabin Camera',
  'Driver App',
  'IoT Device',
  'Patient Monitor Integration',
  'RISA IoT Device'
];

// UUID row id generator using crypto.randomUUID
const generateRowId = (prefix = 'row') => {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) {
    return `${prefix}-${window.crypto.randomUUID()}`;
  }
  return `${prefix}-${Math.random().toString(36).substring(2, 9)}`;
};



// ============================================================================
// DYNAMIC ROW-AWARE QUESTION GENERATOR
// All questions are simple, direct ("What is..."), and adapt to every row in every table
// ============================================================================
export const buildStepQuestions = (isFleet, step, formData = {}, ambulanceTypes = []) => {
  const rawAmbulanceTypes = Array.isArray(ambulanceTypes) && ambulanceTypes.length > 0
    ? ambulanceTypes
    : (formData?.ambulance_types_lookup || []);
  const dynamicAmbulanceTypes = rawAmbulanceTypes.map((t) => {
    if (typeof t === 'object' && t !== null) {
      return t.abbreviation || getAmbulanceTypeAbbreviation(t.name) || t.name;
    }
    return getAmbulanceTypeAbbreviation(t) || String(t);
  });

  if (isFleet) {
    switch (step) {
      // Fleet Step 1: Fleet owner profile
      case 1:
        return [
          { key: 'operator_name', category: 'Fleet owner profile', question: 'What is your fleet business name?', label: 'Business / Fleet Name', placeholder: 'Enter business / fleet name' },
          { key: 'business_license', category: 'Fleet owner profile', question: 'What is your business license number?', label: 'Business License / Registration No.', placeholder: 'Enter registration number' },
          { key: 'category', category: 'Fleet owner profile', question: 'What is your operator category?', label: 'Operator Category', placeholder: 'Select operator category', options: ['Private Operator', 'Hospital Dedicated Vendor', 'Trust / NGO Provider', 'Individual Fleet Owner'] },
          { key: 'fleet_specialization', category: 'Fleet owner profile', question: 'What is your fleet specialization?', label: 'Fleet Specialization', placeholder: 'Select specialization', options: ['Emergency ALS/BLS', 'Patient Transport (Non-Emergency)', 'Intercity Medical Transit', 'Neonatal / Pediatric ICU', 'Events & Industrial Standby'], isMulti: true },
          { key: 'admin_name', category: 'Fleet owner profile', question: 'What is the owner or admin name?', label: 'Admin / Owner Name', placeholder: 'Enter admin / owner full name' },
          { key: 'admin_email', category: 'Fleet owner profile', question: 'What is your official email address?', label: 'Official Email', placeholder: 'Enter official email address' },
          { key: 'admin_phone', category: 'Fleet owner profile', question: 'What is your mobile number?', label: 'Mobile Number', placeholder: 'Enter 10-digit mobile number' },
          { key: 'emergency_contact', category: 'Fleet owner profile', question: 'What is your emergency contact number?', label: 'Emergency 24x7 Contact', placeholder: 'Enter emergency contact number' },
          { key: 'address', category: 'Fleet owner profile', question: 'What is your base address?', label: 'Base Address', placeholder: 'Enter base address' },
          { key: 'city', category: 'Fleet owner profile', question: 'What is your city?', label: 'City', placeholder: 'Enter city' },
          { key: 'state', category: 'Fleet owner profile', question: 'What is your state?', label: 'State', placeholder: 'Enter state' },
          { key: 'pincode', category: 'Fleet owner profile', question: 'What is your PIN code?', label: 'PIN Code', placeholder: 'Enter PIN code' }
        ];

      // Fleet Step 2: Capacity & coverage (Row-wise Fleet Capacities and Operating Areas tables)
      case 2: {
        const qList = [];
        const fcRows = formData.fleet_capacities && formData.fleet_capacities.length > 0 ? formData.fleet_capacities : [{}];
        fcRows.forEach((_, idx) => {
          const rowBadge = fcRows.length > 1 ? ` (Type #${idx + 1})` : '';
          qList.push(
            { table: 'fleet_capacities', rowIndex: idx, field: 'ambulance_type', category: `Fleet capacity${rowBadge}`, question: 'What is the ambulance type?', label: `Ambulance Type${rowBadge}`, placeholder: 'Select type', options: dynamicAmbulanceTypes },
            { table: 'fleet_capacities', rowIndex: idx, field: 'owned', category: `Fleet capacity${rowBadge}`, question: 'How many owned ambulances do you have?', label: `Owned Ambulances${rowBadge}`, placeholder: 'Enter owned count' },
            { table: 'fleet_capacities', rowIndex: idx, field: 'attached', category: `Fleet capacity${rowBadge}`, question: 'How many attached ambulances do you have?', label: `Attached Ambulances${rowBadge}`, placeholder: 'Enter attached count' },
            { table: 'fleet_capacities', rowIndex: idx, field: 'available_daily', category: `Fleet capacity${rowBadge}`, question: 'How many ambulances are available daily?', label: `Daily Available Ambulances${rowBadge}`, placeholder: 'Enter daily available count', canAddRow: idx === fcRows.length - 1, tableType: 'fleet_capacities', addLabel: 'Add another capacity row' }
          );
        });

        qList.push(
          { key: 'primary_base_location', category: 'Operations & coverage', question: 'What is your primary base location?', label: 'Primary Base Location', placeholder: 'Enter primary base location' },
          { key: 'primary_pincode', category: 'Operations & coverage', question: 'What is your primary PIN code?', label: 'Primary PIN Code', placeholder: 'Enter primary PIN code' },
          { key: 'service_radius_km', category: 'Operations & coverage', question: 'What is your service radius in km?', label: 'Service Radius (KM)', placeholder: 'Enter radius in KM' },
          { key: 'operating_hours', category: 'Operations & coverage', question: 'What are your operating hours?', label: 'Operating Hours', placeholder: 'Select operating hours', options: ['24x7 Round the Clock', 'Day Shift (8 AM - 8 PM)', 'Night Shift Dedicated', 'Custom Shift'] },
          { key: 'intercity_available', category: 'Operations & coverage', question: 'Do you offer intercity transfers?', label: 'Intercity Availability', placeholder: 'Select intercity availability', options: ['Yes - Dedicated Intercity Fleet', 'Yes - On Demand', 'No - Local Only'] }
        );

        const oaRows = formData.operating_areas && formData.operating_areas.length > 0 ? formData.operating_areas : [{}];
        oaRows.forEach((_, idx) => {
          const rowBadge = oaRows.length > 1 ? ` (Area #${idx + 1})` : '';
          qList.push(
            { table: 'operating_areas', rowIndex: idx, field: 'area_pin', category: `Top operating areas${rowBadge}`, question: 'What is the operating area and PIN?', label: `Area / PIN${rowBadge}`, placeholder: 'Enter area / PIN' },
            { table: 'operating_areas', rowIndex: idx, field: 'available_ambulances', category: `Top operating areas${rowBadge}`, question: 'How many ambulances are available in this area?', label: `Available Ambulances${rowBadge}`, placeholder: 'Enter count' },
            { table: 'operating_areas', rowIndex: idx, field: 'peak_availability', category: `Top operating areas${rowBadge}`, question: 'What are the peak availability slots?', label: `Peak Availability Slots${rowBadge}`, placeholder: 'Select peak slots', options: ['24x7', 'Morning', 'Noon', 'Evening', 'Night'], isMulti: true },
            { table: 'operating_areas', rowIndex: idx, field: 'type_mix', category: `Top operating areas${rowBadge}`, question: 'What is the ambulance type mix?', label: `Area Type Mix${rowBadge}`, placeholder: 'Select types', options: ['ALS', 'BLS'], isMulti: true, canAddRow: idx === oaRows.length - 1, tableType: 'operating_areas', addLabel: 'Add another operating area' }
          );
        });
        return qList;
      }

      // Fleet Step 3: Rides & clients (Row-wise Top Clients, Pickup Areas, and Corridors tables)
      case 3: {
        const qList = [
          { key: 'completed_rides', nested: 'monthly_rides', category: 'Monthly ride details', question: 'What is your monthly completed rides count?', label: 'Completed Rides / Month', placeholder: 'Enter completed rides count' },
          { key: 'enquiries', nested: 'monthly_rides', category: 'Monthly ride details', question: 'What is your monthly enquiries count?', label: 'Total Enquiries / Month', placeholder: 'Enter enquiries count' },
          { key: 'lost_rides', nested: 'monthly_rides', category: 'Monthly ride details', question: 'What is your monthly lost or rejected rides count?', label: 'Lost / Rejected Rides / Month', placeholder: 'Enter lost rides count' },
          { key: 'average_fare', nested: 'monthly_rides', category: 'Monthly ride details', question: 'What is your average fare in rupees?', label: 'Average Fare (₹)', placeholder: 'Enter average fare in ₹' },
          { key: 'average_trip_km', nested: 'monthly_rides', category: 'Monthly ride details', question: 'What is your average trip distance in km?', label: 'Average Trip Distance (KM)', placeholder: 'Enter average trip km' },
          { key: 'data_confidence', nested: 'monthly_rides', category: 'Monthly ride details', question: 'What is your data confidence level?', label: 'Data Confidence', placeholder: 'Select confidence level', options: ['Estimated / Approximate', 'Moderate Confidence', 'Highly Accurate GPS Logs'] }
        ];

        const tcRows = formData.top_clients && formData.top_clients.length > 0 ? formData.top_clients : [{}];
        tcRows.forEach((_, idx) => {
          const rowBadge = tcRows.length > 1 ? ` (Client #${idx + 1})` : '';
          qList.push(
            { table: 'top_clients', rowIndex: idx, field: 'client', category: `Top clients${rowBadge}`, question: 'What is the client or hospital name?', label: `Client / Hospital Name${rowBadge}`, placeholder: 'Enter hospital or client name' },
            { table: 'top_clients', rowIndex: idx, field: 'type', category: `Top clients${rowBadge}`, question: 'What is the client type?', label: `Client Type${rowBadge}`, placeholder: 'Select client type', options: ['Hospital', 'Nursing Home', 'Corporate / Industrial', 'Event / Diagnostic Center'] },
            { table: 'top_clients', rowIndex: idx, field: 'area', category: `Top clients${rowBadge}`, question: 'What is the client area or locality?', label: `Client Locality / Area${rowBadge}`, placeholder: 'Enter client locality' },
            { table: 'top_clients', rowIndex: idx, field: 'rides_month', category: `Top clients${rowBadge}`, question: 'What is the monthly rides count for this client?', label: `Rides / Month${rowBadge}`, placeholder: 'Enter rides per month' },
            { table: 'top_clients', rowIndex: idx, field: 'average_fare', category: `Top clients${rowBadge}`, question: 'What is the average fare for this client?', label: `Average Fare (₹)${rowBadge}`, placeholder: 'Enter average fare' },
            { table: 'top_clients', rowIndex: idx, field: 'payment', category: `Top clients${rowBadge}`, question: 'What are the payment terms?', label: `Payment Terms${rowBadge}`, placeholder: 'Select payment terms', options: ['Immediate Cash / UPI', 'Credit / Invoice (30 Days)', 'Credit / Invoice (60 Days)'], canAddRow: idx === tcRows.length - 1, tableType: 'top_clients', addLabel: 'Add another client' }
          );
        });

        const paRows = formData.top_pickup_areas && formData.top_pickup_areas.length > 0 ? formData.top_pickup_areas : [{}];
        paRows.forEach((_, idx) => {
          const rowBadge = paRows.length > 1 ? ` (Pickup Area #${idx + 1})` : '';
          qList.push(
            { table: 'top_pickup_areas', rowIndex: idx, field: 'area_pin', category: `Top pickup areas${rowBadge}`, question: 'What is the pickup area and PIN?', label: `Pickup Area / PIN${rowBadge}`, placeholder: 'Enter area / PIN' },
            { table: 'top_pickup_areas', rowIndex: idx, field: 'peak_time', category: `Top pickup areas${rowBadge}`, question: 'What is the peak pickup time?', label: `Peak Time${rowBadge}`, placeholder: 'Enter peak time' },
            { table: 'top_pickup_areas', rowIndex: idx, field: 'type', category: `Top pickup areas${rowBadge}`, question: 'What is the primary ride type?', label: `Ride Type${rowBadge}`, placeholder: 'Select ride type', options: ['Emergency', 'Intercity Transfer', 'Non-Emergency', 'Diagnostic / Dialysis'], canAddRow: idx === paRows.length - 1, tableType: 'top_pickup_areas', addLabel: 'Add another pickup area' }
          );
        });

        const ldRows = formData.long_distance_corridors && formData.long_distance_corridors.length > 0 ? formData.long_distance_corridors : [{}];
        ldRows.forEach((_, idx) => {
          const rowBadge = ldRows.length > 1 ? ` (Corridor #${idx + 1})` : '';
          qList.push(
            { table: 'long_distance_corridors', rowIndex: idx, field: 'pickup', category: `Long-distance corridors${rowBadge}`, question: 'What is the pickup city or location?', label: `Pickup Location${rowBadge}`, placeholder: 'Enter pickup location' },
            { table: 'long_distance_corridors', rowIndex: idx, field: 'destination', category: `Long-distance corridors${rowBadge}`, question: 'What is the destination city?', label: `Destination City${rowBadge}`, placeholder: 'Enter destination city' },
            { table: 'long_distance_corridors', rowIndex: idx, field: 'trips_month', category: `Long-distance corridors${rowBadge}`, question: 'What is the monthly trips count for this corridor?', label: `Trips / Month${rowBadge}`, placeholder: 'Enter trips per month' },
            { table: 'long_distance_corridors', rowIndex: idx, field: 'km', category: `Long-distance corridors${rowBadge}`, question: 'What is the distance in km?', label: `Distance (KM)${rowBadge}`, placeholder: 'Enter km' },
            { table: 'long_distance_corridors', rowIndex: idx, field: 'typical_rate', category: `Long-distance corridors${rowBadge}`, question: 'What is the typical rate in rupees?', label: `Typical Rate (₹)${rowBadge}`, placeholder: 'Enter typical rate in ₹', canAddRow: idx === ldRows.length - 1, tableType: 'long_distance_corridors', addLabel: 'Add another corridor' }
          );
        });

        qList.push({
          key: 'lost_ride_reasons',
          category: 'Operational bottlenecks',
          question: 'What are the reasons for lost rides?',
          label: 'Lost Ride Reasons',
          placeholder: 'Select reasons',
          options: ['No ambulance available', 'Vehicle too far', 'Price disagreement', 'ALS / Doctor unavailable', 'Traffic congestion'],
          isMulti: true
        });

        return qList;
      }

      // Fleet Step 4: Rates
      case 4:
        return [
          { key: 'ambulance_type', nested: 'active_rate_card', category: 'Tariff configuration', question: 'What is the ambulance type?', label: 'Select Ambulance Type', placeholder: 'Select vehicle type', options: dynamicAmbulanceTypes },
          { key: 'base_rate', nested: 'active_rate_card', category: 'Tariff configuration', question: 'What is the base rate in rupees?', label: 'Base Rate (₹)', placeholder: 'Enter base rate (₹)' },
          { key: 'per_km', nested: 'active_rate_card', category: 'Tariff configuration', question: 'What is the per km rate in rupees?', label: 'Per Km (₹)', placeholder: 'Enter per km (₹)' },
          { key: 'others', nested: 'active_rate_card', category: 'Tariff configuration', question: 'What are the other or waiting charges?', label: 'Others', placeholder: 'Enter waiting, oxygen, night charges, etc.' }
        ];

      // Fleet Step 5: Add ambulances (100% Comprehensive Coverage of ALL 9 Cards in Step 5 Manual Form)
      case 5: {
        const qList = [];
        const ambRows = formData.ambulances && formData.ambulances.length > 0 ? formData.ambulances : [{}];
        ambRows.forEach((amb, idx) => {
          const ambBadge = ambRows.length > 1 ? ` (Ambulance #${idx + 1})` : '';

          // ------------------------------------------------------------------
          // Card 1: Vehicle profile (7 fields)
          // ------------------------------------------------------------------
          qList.push(
            { table: 'ambulances', rowIndex: idx, field: 'registration_number', category: `Vehicle profile${ambBadge}`, question: 'What is the vehicle registration number?', label: `Registration Number${ambBadge}`, placeholder: 'Enter registration number (e.g. GJ-01-AB-1234)' },
            { table: 'ambulances', rowIndex: idx, field: 'ambulance_type', category: `Vehicle profile${ambBadge}`, question: 'What is the ambulance type?', label: `Ambulance Type${ambBadge}`, placeholder: 'Select an option', options: dynamicAmbulanceTypes },
            { table: 'ambulances', rowIndex: idx, field: 'ownership', category: `Vehicle profile${ambBadge}`, question: 'What is the ownership model?', label: `Ownership${ambBadge}`, placeholder: 'Select an option', options: OWNERSHIP_OPTIONS },
            { table: 'ambulances', rowIndex: idx, field: 'make_model', category: `Vehicle profile${ambBadge}`, question: 'What is the vehicle make and model?', label: `Make & Model${ambBadge}`, placeholder: 'Enter make & model' },
            { table: 'ambulances', rowIndex: idx, field: 'manufacture_year', category: `Vehicle profile${ambBadge}`, question: 'What is the manufacture year?', label: `Manufacture Year${ambBadge}`, placeholder: 'Enter manufacture year' },
            { table: 'ambulances', rowIndex: idx, field: 'vehicle_code', category: `Vehicle profile${ambBadge}`, question: 'What is the vehicle code or name?', label: `Vehicle Code / Name${ambBadge}`, placeholder: 'Enter vehicle code / name' },
            { table: 'ambulances', rowIndex: idx, field: 'operating_status', category: `Vehicle profile${ambBadge}`, question: 'What is the operating status?', label: `Operating Status${ambBadge}`, placeholder: 'Select an option', options: OPERATING_STATUS_OPTIONS }
          );

          // ------------------------------------------------------------------
          // Card 2: Base location & coverage (5 fields)
          // ------------------------------------------------------------------
          qList.push(
            { table: 'ambulances', rowIndex: idx, field: 'parking_location', category: `Base location & coverage${ambBadge}`, question: 'What is the current parking or base location?', label: `Current Parking / Base Location${ambBadge}`, placeholder: 'Enter current parking / base location' },
            { table: 'ambulances', rowIndex: idx, field: 'lat_long', category: `Base location & coverage${ambBadge}`, question: 'What is the latitude and longitude?', label: `Latitude / Longitude${ambBadge}`, placeholder: 'e.g. 23.0225, 72.5714' },
            { table: 'ambulances', rowIndex: idx, field: 'pincode', category: `Base location & coverage${ambBadge}`, question: 'What is the PIN code?', label: `PIN Code${ambBadge}`, placeholder: 'Enter pin code' },
            { table: 'ambulances', rowIndex: idx, field: 'vehicle_radius', category: `Base location & coverage${ambBadge}`, question: 'What is the vehicle radius in km?', label: `Vehicle Radius (KM)${ambBadge}`, placeholder: 'Enter service radius (km)' },
            { table: 'ambulances', rowIndex: idx, field: 'intercity_available', category: `Base location & coverage${ambBadge}`, question: 'Is this vehicle available for intercity trips?', label: `Intercity Available${ambBadge}`, placeholder: 'Select an option', options: INTERCITY_OPTIONS }
          );

          // ------------------------------------------------------------------
          // Card 3: Usual availability zones (Table)
          // ------------------------------------------------------------------
          const azRows = amb.availability_zones && amb.availability_zones.length > 0 ? amb.availability_zones : [{}];
          azRows.forEach((_, zIdx) => {
            const zBadge = azRows.length > 1 ? ` (Zone #${zIdx + 1})` : '';
            qList.push(
              { table: 'ambulances', rowIndex: idx, subTable: 'availability_zones', subRowIndex: zIdx, field: 'area_pin', category: `Usual availability zones${ambBadge}${zBadge}`, question: 'What is the zone area or PIN?', label: `Zone Area / PIN${ambBadge}${zBadge}`, placeholder: 'Enter area / pin' },
              { table: 'ambulances', rowIndex: idx, subTable: 'availability_zones', subRowIndex: zIdx, field: 'typical_hours', category: `Usual availability zones${ambBadge}${zBadge}`, question: 'What are the typical operating hours in this zone?', label: `Typical Hours${ambBadge}${zBadge}`, placeholder: 'Enter typical hours' },
              { table: 'ambulances', rowIndex: idx, subTable: 'availability_zones', subRowIndex: zIdx, field: 'days', category: `Usual availability zones${ambBadge}${zBadge}`, question: 'What days is this vehicle available in this zone?', label: `Days Available${ambBadge}${zBadge}`, placeholder: 'Enter days', canAddSubRow: zIdx === azRows.length - 1, ambIndex: idx, subTableType: 'availability_zones', addLabel: 'Add another availability zone' }
            );
          });

          // ------------------------------------------------------------------
          // Card 4: Capabilities (2 fields)
          // ------------------------------------------------------------------
          qList.push(
            { table: 'ambulances', rowIndex: idx, field: 'medical_equipment', category: `Capabilities${ambBadge}`, question: 'What medical equipment is onboard?', label: `Medical Equipment${ambBadge}`, placeholder: 'Select equipment', options: MEDICAL_EQUIPMENT_OPTIONS, isMulti: true },
            { table: 'ambulances', rowIndex: idx, field: 'technology', category: `Capabilities${ambBadge}`, question: 'What technology features are installed?', label: `Technology${ambBadge}`, placeholder: 'Select technology features', options: TECHNOLOGY_OPTIONS, isMulti: true }
          );

          // ------------------------------------------------------------------
          // Card 5: Current ride pattern (6 fields)
          // ------------------------------------------------------------------
          qList.push(
            { table: 'ambulances', rowIndex: idx, field: 'rides_month', category: `Current ride pattern${ambBadge}`, question: 'What is the monthly rides count for this ambulance?', label: `Approx. Rides / Month${ambBadge}`, placeholder: 'Enter approx. rides / month' },
            { table: 'ambulances', rowIndex: idx, field: 'average_fare', category: `Current ride pattern${ambBadge}`, question: 'What is the average fare for this ambulance?', label: `Average Fare (₹)${ambBadge}`, placeholder: 'Enter average fare' },
            { table: 'ambulances', rowIndex: idx, field: 'average_trip_km', category: `Current ride pattern${ambBadge}`, question: 'What is the average trip distance in km?', label: `Average Trip KM${ambBadge}`, placeholder: 'Enter average trip km' },
            { table: 'ambulances', rowIndex: idx, field: 'local_ride_pct', category: `Current ride pattern${ambBadge}`, question: 'What is the local ride percentage?', label: `Local Ride %${ambBadge}`, placeholder: 'Enter local ride %' },
            { table: 'ambulances', rowIndex: idx, field: 'intercity_ride_pct', category: `Current ride pattern${ambBadge}`, question: 'What is the intercity ride percentage?', label: `Intercity Ride %${ambBadge}`, placeholder: 'Enter intercity ride %' },
            { table: 'ambulances', rowIndex: idx, field: 'hospital_transfer_pct', category: `Current ride pattern${ambBadge}`, question: 'What is the hospital transfer percentage?', label: `Hospital Transfer %${ambBadge}`, placeholder: 'Enter hospital transfer %' }
          );

          // ------------------------------------------------------------------
          // Card 6: Top ride sources (Table)
          // ------------------------------------------------------------------
          const trsRows = amb.top_ride_sources && amb.top_ride_sources.length > 0 ? amb.top_ride_sources : [{}];
          trsRows.forEach((_, sIdx) => {
            const sBadge = trsRows.length > 1 ? ` (Source #${sIdx + 1})` : '';
            qList.push(
              { table: 'ambulances', rowIndex: idx, subTable: 'top_ride_sources', subRowIndex: sIdx, field: 'hospital_client', category: `Top ride sources${ambBadge}${sBadge}`, question: 'What is the hospital or client name?', label: `Hospital / Client${ambBadge}${sBadge}`, placeholder: 'Enter hospital / client' },
              { table: 'ambulances', rowIndex: idx, subTable: 'top_ride_sources', subRowIndex: sIdx, field: 'area', category: `Top ride sources${ambBadge}${sBadge}`, question: 'What is the client area or locality?', label: `Area${ambBadge}${sBadge}`, placeholder: 'Enter area' },
              { table: 'ambulances', rowIndex: idx, subTable: 'top_ride_sources', subRowIndex: sIdx, field: 'rides_month', category: `Top ride sources${ambBadge}${sBadge}`, question: 'What is the monthly rides count for this source?', label: `Rides / Month${ambBadge}${sBadge}`, placeholder: 'Enter rides / month' },
              { table: 'ambulances', rowIndex: idx, subTable: 'top_ride_sources', subRowIndex: sIdx, field: 'average_fare', category: `Top ride sources${ambBadge}${sBadge}`, question: 'What is the average fare for this source?', label: `Average Fare (₹)${ambBadge}${sBadge}`, placeholder: 'Enter average fare', canAddSubRow: sIdx === trsRows.length - 1, ambIndex: idx, subTableType: 'top_ride_sources', addLabel: 'Add another ride source' }
            );
          });

          // ------------------------------------------------------------------
          // Card 7: Frequent destinations (Table)
          // ------------------------------------------------------------------
          const fdRows = amb.frequent_destinations && amb.frequent_destinations.length > 0 ? amb.frequent_destinations : [{}];
          fdRows.forEach((_, dIdx) => {
            const dBadge = fdRows.length > 1 ? ` (Route #${dIdx + 1})` : '';
            qList.push(
              { table: 'ambulances', rowIndex: idx, subTable: 'frequent_destinations', subRowIndex: dIdx, field: 'origin', category: `Frequent destinations${ambBadge}${dBadge}`, question: 'What is the origin location?', label: `Origin${ambBadge}${dBadge}`, placeholder: 'Enter origin' },
              { table: 'ambulances', rowIndex: idx, subTable: 'frequent_destinations', subRowIndex: dIdx, field: 'destination', category: `Frequent destinations${ambBadge}${dBadge}`, question: 'What is the destination location?', label: `Destination${ambBadge}${dBadge}`, placeholder: 'Enter destination' },
              { table: 'ambulances', rowIndex: idx, subTable: 'frequent_destinations', subRowIndex: dIdx, field: 'trips_month', category: `Frequent destinations${ambBadge}${dBadge}`, question: 'What is the monthly trips count for this route?', label: `Trips / Month${ambBadge}${dBadge}`, placeholder: 'Enter trips / month' },
              { table: 'ambulances', rowIndex: idx, subTable: 'frequent_destinations', subRowIndex: dIdx, field: 'rate', category: `Frequent destinations${ambBadge}${dBadge}`, question: 'What is the trip rate in rupees?', label: `Rate (₹)${ambBadge}${dBadge}`, placeholder: 'Enter rate' },
              { table: 'ambulances', rowIndex: idx, subTable: 'frequent_destinations', subRowIndex: dIdx, field: 'can_add_dest', isDummy: true, canAddSubRow: dIdx === fdRows.length - 1, ambIndex: idx, subTableType: 'frequent_destinations', addLabel: 'Add another destination' }
            );
          });
          // Filter out dummy marker
          const cleanQList = qList.filter(item => !item.isDummy);
          qList.length = 0;
          cleanQList.forEach(item => qList.push(item));

          // ------------------------------------------------------------------
          // Card 8: Primary driver (6 fields)
          // ------------------------------------------------------------------
          qList.push(
            { table: 'ambulances', rowIndex: idx, nested: 'primary_driver', field: 'driver_name', category: `Primary driver${ambBadge}`, question: 'What is the primary driver\'s name?', label: `Driver Name${ambBadge}`, placeholder: 'Enter driver name' },
            { table: 'ambulances', rowIndex: idx, nested: 'primary_driver', field: 'mobile', category: `Primary driver${ambBadge}`, question: 'What is the driver\'s mobile number?', label: `Mobile${ambBadge}`, placeholder: 'Enter mobile' },
            { table: 'ambulances', rowIndex: idx, nested: 'primary_driver', field: 'driving_licence', category: `Primary driver${ambBadge}`, question: 'What is the driving licence number?', label: `Driving Licence${ambBadge}`, placeholder: 'Enter driving licence' },
            { table: 'ambulances', rowIndex: idx, nested: 'primary_driver', field: 'shift', category: `Primary driver${ambBadge}`, question: 'What is the driver\'s shift?', label: `Shift${ambBadge}`, placeholder: 'Select an option', options: SHIFT_OPTIONS },
            { table: 'ambulances', rowIndex: idx, nested: 'primary_driver', field: 'risa_driver_app', category: `Primary driver${ambBadge}`, question: 'Is the RISA driver app installed?', label: `RISA Driver App${ambBadge}`, placeholder: 'Select an option', options: DRIVER_APP_OPTIONS },
            { table: 'ambulances', rowIndex: idx, nested: 'primary_driver', field: 'backup_driver', category: `Primary driver${ambBadge}`, question: 'What is the backup driver\'s name?', label: `Backup Driver${ambBadge}`, placeholder: 'Enter backup driver' }
          );

          // ------------------------------------------------------------------
          // Card 9: Vehicle documents (7 fields)
          // ------------------------------------------------------------------
          qList.push(
            { table: 'ambulances', rowIndex: idx, nested: 'documents', field: 'rc', category: `Vehicle documents${ambBadge}`, question: 'What is the RC document status?', label: `RC Document Status${ambBadge}`, placeholder: 'Select status', options: ['Available / Upload Ready', 'Pending / Under Process', 'Exempted'] },
            { table: 'ambulances', rowIndex: idx, nested: 'documents', field: 'insurance', category: `Vehicle documents${ambBadge}`, question: 'What is the insurance policy status?', label: `Insurance Status${ambBadge}`, placeholder: 'Select status', options: ['Active Policy', 'Under Renewal', 'Pending'] },
            { table: 'ambulances', rowIndex: idx, nested: 'documents', field: 'fitness_certificate', category: `Vehicle documents${ambBadge}`, question: 'What is the fitness certificate status?', label: `Fitness Certificate Status${ambBadge}`, placeholder: 'Select status', options: ['Valid Certificate', 'Due for Renewal', 'Pending'] },
            { table: 'ambulances', rowIndex: idx, nested: 'documents', field: 'permit', category: `Vehicle documents${ambBadge}`, question: 'What is the vehicle permit status?', label: `Permit Status${ambBadge}`, placeholder: 'Select status', options: ['Commercial Permit Active', 'State Permit', 'Pending'] },
            { table: 'ambulances', rowIndex: idx, nested: 'documents', field: 'puc', category: `Vehicle documents${ambBadge}`, question: 'What is the PUC emission certificate status?', label: `PUC Certificate Status${ambBadge}`, placeholder: 'Select status', options: ['Valid Certificate', 'Pending Test'] },
            { table: 'ambulances', rowIndex: idx, nested: 'documents', field: 'driver_licence', category: `Vehicle documents${ambBadge}`, question: 'What is the commercial driver licence status?', label: `Driver Licence Status${ambBadge}`, placeholder: 'Select status', options: ['Valid Commercial Licence', 'Heavy Vehicle Endorsement'] },
            { table: 'ambulances', rowIndex: idx, nested: 'documents', field: 'ambulance_registration', category: `Vehicle documents${ambBadge}`, question: 'What is the ambulance registration certificate status?', label: `Ambulance Registration Status${ambBadge}`, placeholder: 'Select status', options: ['Official Ambulance Endorsed', 'Application Acknowledged'], canAddRow: idx === ambRows.length - 1, tableType: 'ambulances', addLabel: 'Add another ambulance' }
          );
        });

        return qList;
      }

      default:
        return [];
    }
  } else {
    // HOSPITAL
    switch (step) {
      // Hospital Step 1: Hospital profile
      case 1:
        return [
          { key: 'hospital_name', category: 'Hospital profile', question: 'What is your hospital name?', label: 'Hospital Name', placeholder: 'Enter hospital name' },
          { key: 'hospital_types', category: 'Hospital profile', question: 'What is your hospital type?', label: 'Hospital Type', placeholder: 'Select hospital type', options: ['Multi-speciality', 'Trauma Center', 'Cardiac Hospital', 'Mother & Child', 'Nursing Home', 'Diagnostic / Day Care', 'Cancer / Oncology', 'Orthopaedic'], isMulti: true },
          { key: 'city', category: 'Hospital profile', question: 'What is your city?', label: 'City', placeholder: 'Enter city' },
          { key: 'pincode', category: 'Hospital profile', question: 'What is your PIN code?', label: 'PIN Code', placeholder: 'Enter PIN code' },
          { key: 'number_of_beds', category: 'Hospital profile', question: 'What is the total number of beds?', label: 'No. of Beds', placeholder: 'Enter number of beds' },
          { key: 'google_coordinates', category: 'Hospital profile', question: 'What is the location or coordinates?', label: 'Location', placeholder: 'e.g. 23.0225, 72.5714' },
          // { key: 'google_maps_link', category: 'Hospital profile', question: 'What is the Google Maps link?', label: 'Google Maps Link', placeholder: 'Enter Google Maps link' },
          { key: 'full_address', category: 'Hospital profile', question: 'What is the full address?', label: 'Full Address', placeholder: 'Enter full address' }
        ];

      // Hospital Step 2: Facilities & contacts (Row-wise Contacts Table)
      case 2: {
        const qList = [
          { key: 'emergency_facilities', category: 'Emergency capability', question: 'What emergency facilities do you have?', label: 'Facilities', placeholder: 'Select facilities', options: ['Emergency Department', 'Trauma Center', 'ICU', 'NICU / PICU', 'Cath Lab'], isMulti: true },
          { key: 'icu_beds', category: 'Emergency capability', question: 'What is the ICU bed count?', label: 'ICU Beds', placeholder: 'Enter ICU beds' },
          { key: 'emergency_beds', category: 'Emergency capability', question: 'What is the emergency bed count?', label: 'Emergency Beds', placeholder: 'Enter emergency beds' },
          { key: 'total_beds', category: 'Emergency capability', question: 'What is the total bed count?', label: 'Total Beds', placeholder: 'Enter total beds' }
        ];

        const contactRows = formData.contacts && formData.contacts.length > 0 ? formData.contacts : [{}];
        contactRows.forEach((_, idx) => {
          const contactBadge = contactRows.length > 1 ? ` (Contact #${idx + 1})` : '';
          qList.push(
            { table: 'contacts', rowIndex: idx, field: 'role', category: `Key hospital contacts${contactBadge}`, question: 'What is the contact person\'s role?', label: `Designation / Role${contactBadge}`, placeholder: 'Enter designation / role' },
            { table: 'contacts', rowIndex: idx, field: 'name', category: `Key hospital contacts${contactBadge}`, question: 'What is the contact person\'s name?', label: `Full Name${contactBadge}`, placeholder: 'Enter full name' },
            { table: 'contacts', rowIndex: idx, field: 'mobile', category: `Key hospital contacts${contactBadge}`, question: 'What is their mobile number?', label: `Mobile Number${contactBadge}`, placeholder: 'Enter 10-digit mobile number' },
            { table: 'contacts', rowIndex: idx, field: 'email', category: `Key hospital contacts${contactBadge}`, question: 'What is their email address?', label: `Email Address${contactBadge}`, placeholder: 'Enter email address' },
            { table: 'contacts', rowIndex: idx, field: 'is_primary', category: `Key hospital contacts${contactBadge}`, question: 'Is this the primary contact?', label: `Primary Contact?${contactBadge}`, placeholder: 'Select option', options: ['Yes', 'No'], canAddRow: idx === contactRows.length - 1, tableType: 'contacts', addLabel: 'Add another contact' }
          );
        });
        return qList;
      }

      // Hospital Step 3: Ambulance requirements (Row-wise Monthly Volume Table)
      case 3: {
        const qList = [];
        const monthRows = formData.monthly_demands && formData.monthly_demands.length > 0 ? formData.monthly_demands : [{}];
        monthRows.forEach((_, idx) => {
          const monthBadge = monthRows.length > 1 ? ` (Month #${idx + 1})` : '';
          qList.push(
            { table: 'monthly_demands', rowIndex: idx, field: 'month', category: `Monthly transport volume${monthBadge}`, question: 'What is the month?', label: `Month${monthBadge}`, placeholder: 'YYYY-MM' },
            { table: 'monthly_demands', rowIndex: idx, field: 'ambulance_type', category: `Monthly transport volume${monthBadge}`, question: 'What is the ambulance type?', label: `Ambulance Type${monthBadge}`, placeholder: 'Select type', options: dynamicAmbulanceTypes },
            { table: 'monthly_demands', rowIndex: idx, field: 'total', category: `Monthly transport volume${monthBadge}`, question: 'What is the total calls count?', label: `Total Calls${monthBadge}`, placeholder: 'Total calls' },
            { table: 'monthly_demands', rowIndex: idx, field: 'inbound', category: `Monthly transport volume${monthBadge}`, question: 'What is the inbound calls count?', label: `Inbound Calls${monthBadge}`, placeholder: 'Inbound calls' },
            { table: 'monthly_demands', rowIndex: idx, field: 'outbound', category: `Monthly transport volume${monthBadge}`, question: 'What is the outbound calls count?', label: `Outbound Calls${monthBadge}`, placeholder: 'Outbound calls' },
            { table: 'monthly_demands', rowIndex: idx, field: 'unserved', category: `Monthly transport volume${monthBadge}`, question: 'What is the unserved calls count?', label: `Unserved Calls${monthBadge}`, placeholder: 'Unserved calls', canAddRow: idx === monthRows.length - 1, tableType: 'monthly_demands', addLabel: 'Add another month' }
          );
        });

        qList.push(
          { key: 'peak_time_slots', category: 'Peak demand & turnaround', question: 'What are your peak time slots?', label: 'Peak Time Slots', placeholder: 'Select peak time slots', options: ['12 AM–6 AM', '6 AM–12 PM', '12 PM–6 PM', '6 PM–12 AM'], isMulti: true },
          { key: 'custom_peak_time', category: 'Peak demand & turnaround', question: 'What is your custom peak time?', label: 'Custom Peak Time', placeholder: 'Enter custom peak time' },
          { key: 'peak_days', category: 'Peak demand & turnaround', question: 'What are your peak days?', label: 'Peak Days', placeholder: 'Select an option', options: ['All days', 'Monday - Friday (Weekdays)', 'Saturday - Sunday (Weekends)', 'Festival / Special days'] },
          { key: 'approx_peak_calls_per_hour', category: 'Peak demand & turnaround', question: 'What are the peak calls per hour?', label: 'Approx. Peak Calls / Hour', placeholder: 'Enter peak calls / hour' },
          { key: 'unserved_delay_reasons', category: 'Dispatch bottlenecks', question: 'What are the reasons for delays or unserved calls?', label: 'Common Reasons for Delays', placeholder: 'Select reasons', options: ['No ambulance available', 'Delayed arrival', 'ALS unavailable', 'High price', 'Driver unavailable', 'Long distance'], isMulti: true },
          { key: 'unserved_delay_other_reason', category: 'Dispatch bottlenecks', question: 'What is the other specific delay reason?', label: 'Other Specific Reason', placeholder: 'Enter other specific reason' }
          // Ambulance requirement mix questions hidden as per requirement
          // { key: 'mix_bls_percent', category: 'Ambulance requirement mix', question: 'What is the BLS percentage?', label: 'BLS (Basic Life Support) %', placeholder: 'Enter percentage' },
          // { key: 'mix_als_percent', category: 'Ambulance requirement mix', question: 'What is the ALS percentage?', label: 'ALS (Advanced Life Support) %', placeholder: 'Enter percentage' },
          // { key: 'mix_icu_percent', category: 'Ambulance requirement mix', question: 'What is the ICU percentage?', label: 'ICU / Critical Transport %', placeholder: 'Enter percentage' },
          // { key: 'mix_other_percent', category: 'Ambulance requirement mix', question: 'What is the other transport percentage?', label: 'Other Specialist Transport %', placeholder: 'Enter percentage' }
        );
        return qList;
      }

      // Hospital Step 4: Areas & destinations (Row-wise Primary Areas and Destination Corridors Tables)
      case 4: {
        const qList = [];
        const areaRows = formData.area_demands && formData.area_demands.length > 0 ? formData.area_demands : [{}];
        areaRows.forEach((_, idx) => {
          const areaBadge = areaRows.length > 1 ? ` (Area #${idx + 1})` : '';
          qList.push(
            { table: 'area_demands', rowIndex: idx, field: 'area', category: `Primary service areas & PIN codes${areaBadge}`, question: 'What is the area name?', label: `Area Name${areaBadge}`, placeholder: 'Area name' },
            { table: 'area_demands', rowIndex: idx, field: 'pincode', category: `Primary service areas & PIN codes${areaBadge}`, question: 'What is the PIN code?', label: `PIN Code${areaBadge}`, placeholder: 'PIN code' },
            { table: 'area_demands', rowIndex: idx, field: 'city', category: `Primary service areas & PIN codes${areaBadge}`, question: 'What is the city?', label: `City${areaBadge}`, placeholder: 'City' },
            { table: 'area_demands', rowIndex: idx, field: 'calls_per_month', category: `Primary service areas & PIN codes${areaBadge}`, question: 'What is the monthly calls count?', label: `Calls / Month${areaBadge}`, placeholder: 'Calls' },
            { table: 'area_demands', rowIndex: idx, field: 'inbound_percent', category: `Primary service areas & PIN codes${areaBadge}`, question: 'What is the inbound percentage?', label: `Inbound %${areaBadge}`, placeholder: 'Inbound %' },
            { table: 'area_demands', rowIndex: idx, field: 'primary_type', category: `Primary service areas & PIN codes${areaBadge}`, question: 'What is the primary patient condition or dispatch type?', label: `Primary Condition / Type${areaBadge}`, placeholder: 'Select type', options: ['Neuro', 'Ortho', 'Lung Disease', 'BLS - Big', 'ALS', 'Freezer', 'HFB', 'AIR AMB', 'NICU', 'Air Cargo', 'Train AMB', 'ACLS', 'BLS - Small', 'Cardiac', 'Dialysis', 'Chemotherapy', 'Diagnostic', 'Hospital Transfer', 'Discharge', 'Pick-Up', 'Dead body', 'Stand By', 'Event', 'Others'] },
            { table: 'area_demands', rowIndex: idx, field: 'current_supply', category: `Primary service areas & PIN codes${areaBadge}`, question: 'What is the current ambulance supply?', label: `Current Supply${areaBadge}`, placeholder: 'Select supply', options: ['Adequate (0-10 min delay)', 'Moderate delay (10-25 min)', 'Severe shortage (>25 min)'], canAddRow: idx === areaRows.length - 1, tableType: 'area_demands', addLabel: 'Add another area' }
          );
        });

        const destRows = formData.destination_corridors && formData.destination_corridors.length > 0 ? formData.destination_corridors : [{}];
        destRows.forEach((_, idx) => {
          const destBadge = destRows.length > 1 ? ` (Route #${idx + 1})` : '';
          qList.push(
            { table: 'destination_corridors', rowIndex: idx, field: 'destination', category: `Destination corridors${destBadge}`, question: 'What is the destination facility or city?', label: `Destination / Facility / City${destBadge}`, placeholder: 'Destination facility / city' },
            { table: 'destination_corridors', rowIndex: idx, field: 'trips_per_month', category: `Destination corridors${destBadge}`, question: 'What is the monthly trips count?', label: `Trips / Month${destBadge}`, placeholder: 'Trips' },
            { table: 'destination_corridors', rowIndex: idx, field: 'distance_km', category: `Destination corridors${destBadge}`, question: 'What is the distance in km?', label: `Approx. Distance (km)${destBadge}`, placeholder: 'km', canAddRow: idx === destRows.length - 1, tableType: 'destination_corridors', addLabel: 'Add another route' }
          );
        });

        qList.push(
          { key: 'service_gaps', category: 'Current service gaps', question: 'What are your service gaps?', label: 'Service Gaps Experienced', placeholder: 'Select service gaps', options: ['Availability', 'Response time', 'Equipment readiness', 'Pricing', 'Intercity service'], isMulti: true },
          { key: 'service_gap_details', category: 'Current service gaps', question: 'What are the specific corridor details or delays?', label: 'Additional Details & Specific Corridors', placeholder: 'Describe frequent delays or deficits...' }
        );
        return qList;
      }

      default:
        return [];
    }
  }
};

export const HospitalOnboardingVoicePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { formData, updateFields, setCurrentStep: setContextStep, submitApplication, ambulanceTypeNames, applicationId, showToast } = useHospitalOnboarding();

  // Determine partner type: 'fleet' or 'hospital'
  const partnerTypeParam = searchParams.get('type');
  const partnerType =
    partnerTypeParam ||
    formData.partnerType ||
    localStorage.getItem('risa_partner_type') ||
    'hospital';
  const isFleet = partnerType === 'fleet';

  const urlAppId = searchParams.get('applicationId');
  const storedAppId = typeof localStorage !== 'undefined' ? localStorage.getItem('risa_current_application_id') : null;
  const rawAppId =
    urlAppId ||
    applicationId ||
    storedAppId ||
    formData.application_id;

  const isValidAppId =
    typeof rawAppId === 'string' &&
    rawAppId.trim().length > 0 &&
    !rawAppId.startsWith('RISA-APP-');

  const activeAppId = isValidAppId ? rawAppId : null;
  const isRegistered = !!activeAppId;

  // Application Guard: Voice onboarding requires prior partner registration
  useEffect(() => {
    if (!isRegistered) {
      if (showToast) {
        showToast('Please complete initial partner registration first before voice intake.', 'error');
      }
      navigate(`/hospital/register?type=${partnerType}`, { replace: true });
    }
  }, [isRegistered, partnerType, navigate, showToast]);

  // Active steps list
  const stepsList = isFleet ? FLEET_STEPS : HOSPITAL_STEPS;

  // Active step (1 to 6 for Fleet, 1 to 5 for Hospital)
  const initialStepParam = parseInt(searchParams.get('step') || '1', 10);
  const [currentStep, setCurrentStep] = useState(
    initialStepParam >= 1 && initialStepParam <= stepsList.length ? initialStepParam : 1
  );

  // Active question index within current step
  const [questionIdx, setQuestionIdx] = useState(0);

  // Voice listening & speech recognition state
  const isSpeechSupported = isSpeechRecognitionSupported();
  const [isListening, setIsListening] = useState(false);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const recognitionRef = useRef(null);

  // Current Step and Current Question object (Dynamically row-aware)
  const currentStepObj = stepsList.find((s) => s.id === currentStep) || stepsList[0];
  const stepQuestions = useMemo(() => {
    return buildStepQuestions(isFleet, currentStep, formData, ambulanceTypeNames);
  }, [isFleet, currentStep, formData, ambulanceTypeNames]);

  const isReviewStep = currentStep === stepsList.length;
  const currentQ = stepQuestions[questionIdx] || null;

  // Sync step if URL search param changes
  useEffect(() => {
    const sParam = searchParams.get('step');
    if (sParam) {
      const sNum = parseInt(sParam, 10);
      if (!isNaN(sNum) && sNum >= 1 && sNum <= stepsList.length) {
        setCurrentStep(sNum);
        setQuestionIdx(0);
      }
    }
  }, [searchParams, stepsList.length]);

  // Sync partnerType if URL parameter differs from context
  useEffect(() => {
    if (partnerTypeParam && partnerTypeParam !== formData.partnerType) {
      updateFields({ partnerType: partnerTypeParam });
      localStorage.setItem('risa_partner_type', partnerTypeParam);
    }
  }, [partnerTypeParam, formData.partnerType, updateFields]);

  // Helper: Extract current saved value from formData
  const getSavedValueForQuestion = (q) => {
    if (!q) return '';

    // Ambulance-specific fields (with subTables or nested properties)
    if (q.table === 'ambulances' && typeof q.rowIndex === 'number') {
      const amb = (formData.ambulances || [])[q.rowIndex] || {};

      if (q.subTable && typeof q.subRowIndex === 'number' && q.field) {
        const subArr = amb[q.subTable] || [];
        const subRow = subArr[q.subRowIndex] || {};
        return subRow[q.field] || '';
      }

      if (q.nested && q.field) {
        return amb[q.nested]?.[q.field] || '';
      }

      if (q.field) {
        const v = amb[q.field];
        return Array.isArray(v) ? v.join(', ') : v || '';
      }
    }

    // Other table-based questions (with rowIndex)
    if (q.table && typeof q.rowIndex === 'number' && q.field) {
      const arr = formData[q.table] || [];
      const row = arr[q.rowIndex] || {};
      if (q.nested) {
        const nestedObj = row[q.nested] || {};
        const v = nestedObj[q.field];
        return Array.isArray(v) ? v.join(', ') : v || '';
      }
      const v = row[q.field];
      return Array.isArray(v) ? v.join(', ') : v || '';
    }

    // Nested non-table questions (like monthly_rides, active_rate_card)
    if (q.nested && q.key) {
      const parentObj = formData[q.nested] || {};
      const v = parentObj[q.key];
      return Array.isArray(v) ? v.join(', ') : v || '';
    }

    // Direct mappings
    if (q.key) {
      const v = formData[q.key];
      return Array.isArray(v) ? v.join(', ') : v || '';
    }

    return '';
  };

  // Sync currentAnswer whenever question or step changes
  useEffect(() => {
    if (currentQ) {
      setCurrentAnswer(getSavedValueForQuestion(currentQ));
    }
  }, [currentStep, questionIdx, currentQ]);

  // Keep onboarding context step synced
  useEffect(() => {
    setContextStep(currentStep);
  }, [currentStep, setContextStep]);

  // Web Speech API Initialization
  useEffect(() => {
    if (!isSpeechSupported) return;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-IN';

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onresult = (event) => {
        let transcript = '';
        for (let i = 0; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        if (transcript) {
          setCurrentAnswer(transcript);
        }
      };

      recognition.onerror = () => {
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {}
      }
    };
  }, [isSpeechSupported]);

  // Toggle microphone - safe guard when speech recognition is unavailable
  const toggleListening = () => {
    if (!isSpeechSupported || !recognitionRef.current) {
      return;
    }

    if (isListening) {
      try {
        recognitionRef.current.stop();
      } catch (e) {}
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (err) {
        setIsListening(false);
      }
    }
  };

  // Read question aloud using SpeechSynthesis
  const handleReadAloud = () => {
    if ('speechSynthesis' in window && currentQ) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(currentQ.question);
      utterance.lang = 'en-IN';
      utterance.rate = 0.95;
      window.speechSynthesis.speak(utterance);
    }
  };

  // Save current answer into formData with voice-answer-to-field parsing
  const saveCurrentAnswer = (valToSave = currentAnswer) => {
    if (!currentQ) return;
    const rawTrimmed = (valToSave || '').trim();
    if (!rawTrimmed) return;
    const cleanVal = parseVoiceAnswer(currentQ, rawTrimmed);
    if (!cleanVal) return;

    const updates = {};

    // 1. Ambulance-specific fields (with subTables or nested properties)
    if (currentQ.table === 'ambulances' && typeof currentQ.rowIndex === 'number') {
      const ambList = [...(formData.ambulances || [])];
      while (ambList.length <= currentQ.rowIndex) {
        ambList.push({
          id: generateRowId('amb'),
          availability_zones: [{ id: generateRowId('az'), area_pin: '', typical_hours: '', days: '' }],
          top_ride_sources: [{ id: generateRowId('trs'), hospital_client: '', area: '', rides_month: '', average_fare: '' }],
          frequent_destinations: [{ id: generateRowId('fd'), origin: '', destination: '', trips_month: '', rate: '' }],
          primary_driver: { driver_name: '', mobile: '', driving_licence: '', shift: '', risa_driver_app: '', backup_driver: '' },
          documents: {}
        });
      }
      const amb = { ...(ambList[currentQ.rowIndex] || {}) };

      // Sub-table inside ambulance (like availability_zones, top_ride_sources, frequent_destinations)
      if (currentQ.subTable && typeof currentQ.subRowIndex === 'number' && currentQ.field) {
        const subArr = [...(amb[currentQ.subTable] || [])];
        while (subArr.length <= currentQ.subRowIndex) {
          subArr.push({ id: generateRowId(currentQ.subTable) });
        }
        subArr[currentQ.subRowIndex] = {
          ...(subArr[currentQ.subRowIndex] || {}),
          [currentQ.field]: cleanVal
        };
        amb[currentQ.subTable] = subArr;
      }

      // Nested inside ambulance (like primary_driver, documents)
      else if (currentQ.nested && currentQ.field) {
        amb[currentQ.nested] = {
          ...(amb[currentQ.nested] || {}),
          [currentQ.field]: cleanVal
        };
      }

      // Direct ambulance property
      else if (currentQ.field) {
        if (currentQ.isMulti) {
          amb[currentQ.field] = cleanVal.split(',').map((s) => s.trim()).filter(Boolean);
        } else {
          amb[currentQ.field] = cleanVal;
        }
      }

      ambList[currentQ.rowIndex] = amb;
      updates.ambulances = ambList;
    }

    // 2. Other Table-based questions (with rowIndex)
    else if (currentQ.table && typeof currentQ.rowIndex === 'number' && currentQ.field) {
      const arr = [...(formData[currentQ.table] || [])];
      while (arr.length <= currentQ.rowIndex) {
        arr.push({ id: generateRowId(currentQ.table) });
      }
      const row = { ...(arr[currentQ.rowIndex] || {}) };
      const val = currentQ.isMulti
        ? cleanVal.split(',').map((s) => s.trim()).filter(Boolean)
        : cleanVal;

      if (currentQ.nested) {
        row[currentQ.nested] = {
          ...(row[currentQ.nested] || {}),
          [currentQ.field]: val
        };
      } else {
        row[currentQ.field] = val;
      }
      arr[currentQ.rowIndex] = row;
      updates[currentQ.table] = arr;
    }

    // 3. Nested non-table questions (like monthly_rides, active_rate_card)
    else if (currentQ.nested && currentQ.key) {
      const parentObj = { ...(formData[currentQ.nested] || {}) };
      parentObj[currentQ.key] = currentQ.isMulti
        ? cleanVal.split(',').map((s) => s.trim()).filter(Boolean)
        : cleanVal;
      updates[currentQ.nested] = parentObj;

      // Special sync for active_rate_card to rate_cards array
      if (currentQ.nested === 'active_rate_card') {
        const rc = [...(formData.rate_cards || [])];
        if (rc.length === 0) rc.push(parentObj);
        else rc[0] = { ...rc[0], [currentQ.key]: cleanVal };
        updates.rate_cards = rc;
      }
    }

    // 4. Direct mappings
    else if (currentQ.key) {
      if (currentQ.isMulti) {
        updates[currentQ.key] = cleanVal.split(',').map((s) => s.trim()).filter(Boolean);
      } else {
        updates[currentQ.key] = cleanVal;
      }
    }

    updateFields(updates);
  };

  // Advance to next question or next step
  const handleConfirmNext = async () => {
    saveCurrentAnswer();

    if (recognitionRef.current && isListening) {
      try {
        recognitionRef.current.stop();
      } catch (e) {}
    }
    setIsListening(false);

    if (questionIdx < stepQuestions.length - 1) {
      setQuestionIdx((prev) => prev + 1);
    } else {
      // Step completed -> Advance to next step
      if (currentStep < stepsList.length) {
        const nextStep = currentStep + 1;
        setCurrentStep(nextStep);
        setQuestionIdx(0);
        navigate(`/hospital/onboarding/voice?type=${partnerType}&step=${nextStep}`);
      } else {
        // Last step completed -> Submit
        const res = await submitApplication();
        if (res) {
          navigate(`/hospital/onboarding/submitted?type=${partnerType}`);
        }
      }
    }
  };

  // Add another row dynamically to any table
  const handleAddTableRow = (tableType) => {
    saveCurrentAnswer();
    const currentList = [...(formData[tableType] || [])];
    let newRow = { id: generateRowId(tableType) };

    if (tableType === 'area_demands') {
      newRow = { id: generateRowId('area'), area: '', pincode: '', city: '', calls_per_month: '', inbound_percent: '', primary_type: '', current_supply: '' };
    } else if (tableType === 'destination_corridors') {
      newRow = { id: generateRowId('dest'), destination: '', trips_per_month: '', distance_km: '' };
    } else if (tableType === 'contacts') {
      newRow = { id: generateRowId('contact'), role: '', name: '', mobile: '', email: '', is_primary: 'No' };
    } else if (tableType === 'monthly_demands') {
      newRow = { id: generateRowId('month'), month: '', total: '', inbound: '', outbound: '', als: '', bls: '', unserved: '' };
    } else if (tableType === 'fleet_capacities') {
      newRow = { id: generateRowId('fc'), ambulance_type: '', owned: '', attached: '', available_daily: '' };
    } else if (tableType === 'operating_areas') {
      newRow = { id: generateRowId('oa'), area_pin: '', available_ambulances: '', peak_availability: [], type_mix: [] };
    } else if (tableType === 'top_clients') {
      newRow = { id: generateRowId('tc'), client: '', type: 'Hospital', area: '', rides_month: '', average_fare: '', payment: '' };
    } else if (tableType === 'top_pickup_areas') {
      newRow = { id: generateRowId('pa'), area_pin: '', peak_time: '', type: 'Emergency' };
    } else if (tableType === 'long_distance_corridors') {
      newRow = { id: generateRowId('ld'), pickup: '', destination: '', trips_month: '', km: '', typical_rate: '' };
    } else if (tableType === 'ambulances') {
      newRow = {
        id: generateRowId('amb'),
        registration_number: '',
        ambulance_type: '',
        ownership: '',
        make_model: '',
        manufacture_year: '',
        vehicle_code: '',
        operating_status: '',
        parking_location: '',
        lat_long: '',
        pincode: '',
        vehicle_radius: '',
        intercity_available: '',
        availability_zones: [{ id: generateRowId('az'), area_pin: '', typical_hours: '', days: '' }],
        medical_equipment: [],
        technology: [],
        rides_month: '',
        average_fare: '',
        average_trip_km: '',
        local_ride_pct: '',
        intercity_ride_pct: '',
        hospital_transfer_pct: '',
        top_ride_sources: [{ id: generateRowId('trs'), hospital_client: '', area: '', rides_month: '', average_fare: '' }],
        frequent_destinations: [{ id: generateRowId('fd'), origin: '', destination: '', trips_month: '', rate: '' }],
        primary_driver: { driver_name: '', mobile: '', driving_licence: '', shift: '', risa_driver_app: '', backup_driver: '' },
        documents: {}
      };
    }

    currentList.push(newRow);
    updateFields({ [tableType]: currentList });
    setQuestionIdx((prev) => prev + 1);
  };

  // Add another sub-row inside an ambulance (e.g. availability zones, top ride sources, frequent destinations)
  const handleAddSubTableRow = (ambIndex, subTableType) => {
    saveCurrentAnswer();
    const ambList = [...(formData.ambulances || [])];
    const amb = { ...(ambList[ambIndex] || {}) };
    const currentSubList = [...(amb[subTableType] || [])];
    let newRow = { id: generateRowId(subTableType) };

    if (subTableType === 'availability_zones') {
      newRow = { id: generateRowId('az'), area_pin: '', typical_hours: '', days: '' };
    } else if (subTableType === 'top_ride_sources') {
      newRow = { id: generateRowId('trs'), hospital_client: '', area: '', rides_month: '', average_fare: '' };
    } else if (subTableType === 'frequent_destinations') {
      newRow = { id: generateRowId('fd'), origin: '', destination: '', trips_month: '', rate: '' };
    }

    currentSubList.push(newRow);
    amb[subTableType] = currentSubList;
    ambList[ambIndex] = amb;
    updateFields({ ambulances: ambList });
    setQuestionIdx((prev) => prev + 1);
  };

  // Skip question
  const handleSkip = () => {
    if (questionIdx < stepQuestions.length - 1) {
      setQuestionIdx((prev) => prev + 1);
    } else {
      if (currentStep < stepsList.length) {
        const nextStep = currentStep + 1;
        setCurrentStep(nextStep);
        setQuestionIdx(0);
        navigate(`/hospital/onboarding/voice?type=${partnerType}&step=${nextStep}`);
      } else {
        navigate(`/hospital/onboarding/submitted?type=${partnerType}`);
      }
    }
  };

  // Go back
  const handleBack = () => {
    if (questionIdx > 0) {
      setQuestionIdx((prev) => prev - 1);
    } else if (currentStep > 1) {
      const prevStep = currentStep - 1;
      const prevStepQuestions = buildStepQuestions(isFleet, prevStep, formData, ambulanceTypeNames);
      setCurrentStep(prevStep);
      setQuestionIdx(Math.max(0, prevStepQuestions.length - 1));
      navigate(`/hospital/onboarding/voice?type=${partnerType}&step=${prevStep}`);
    }
  };

  // Jump to specific step from top progress bar
  const handleStepJump = (targetStepId) => {
    saveCurrentAnswer();
    setCurrentStep(targetStepId);
    setQuestionIdx(0);
    navigate(`/hospital/onboarding/voice?type=${partnerType}&step=${targetStepId}`);
  };

  // Switch to Manual Entry on current step
  const handleSwitchToManual = () => {
    saveCurrentAnswer();
    navigate(`/hospital/onboarding/manual?applicationId=${activeAppId || ''}&type=${partnerType}&step=${currentStep}`);
  };

  // Final submission on review step
  const handleFinalSubmit = async () => {
    const res = await submitApplication();
    if (res) {
      navigate(`/hospital/onboarding/submitted?type=${partnerType}`);
    }
  };

  const isFirst = currentStep === 1 && questionIdx === 0;

  if (!isRegistered) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center p-6 bg-white rounded-2xl shadow-sm border border-slate-200">
          <p className="text-sm font-medium text-slate-700">Please complete initial partner registration first. Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex flex-col font-sans"
      style={{
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)'
      }}
    >
      {/* Top Main Navigation Header */}
      <OnboardingHeader />

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* ====================================================================
            TOP PROGRESS BAR (Fleet: 6 Steps | Hospital: 5 Steps)
            Equal grid cards with smart ambulance blue active state matching mockup
        ==================================================================== */}
        <div className="w-full mb-8">
          <div
            className={`grid grid-cols-2 sm:grid-cols-3 ${
              isFleet ? 'lg:grid-cols-6' : 'lg:grid-cols-5'
            } gap-2 sm:gap-3`}
          >
            {stepsList.map((step) => {
              const isActive = step.id === currentStep;
              const isCompleted = step.id < currentStep;

              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => handleStepJump(step.id)}
                  className={`w-full flex items-center justify-center gap-2 py-2.5 sm:py-3 px-3 rounded-lg border text-xs sm:text-sm transition-all duration-150 select-none cursor-pointer ${
                    isActive
                      ? 'bg-blue-50 border-2 border-blue-600 text-blue-700 shadow-2xs font-bold'
                      : isCompleted
                      ? 'bg-white border border-slate-200 text-slate-700 hover:border-slate-300 font-medium'
                      : 'bg-white border border-slate-200/80 text-slate-400 hover:text-slate-600 font-normal'
                  }`}
                >
                  {/* Step circle number */}
                  <span
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] shrink-0 ${
                      isActive
                        ? 'bg-blue-600 text-white font-bold'
                        : isCompleted
                        ? 'bg-blue-50 border border-blue-300 text-blue-600 font-bold'
                        : 'border border-slate-300 text-slate-400 font-normal'
                    }`}
                  >
                    {isCompleted ? <Check className="w-3 h-3 stroke-[2.5]" /> : step.id}
                  </span>

                  {/* Step label */}
                  <span className="truncate">{step.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ====================================================================
            STEP HEADER ROW (Left: Step Title & Blue Accent | Right: Manual entry)
        ==================================================================== */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-blue-600 block mb-1">
              STEP {currentStep} OF {stepsList.length}
            </span>
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-800 leading-tight">
              {currentStepObj.label}
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 mt-1 font-normal">
              {currentStepObj.desc}
            </p>
            {/* Smart Ambulance Blue Accent Line */}
            <div className="w-14 sm:w-16 h-0.5 bg-blue-600 mt-2 rounded-full" />
          </div>

          {/* Right Action: [ ✏️ Manual entry ] Button */}
          <button
            type="button"
            onClick={handleSwitchToManual}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-xs font-medium text-slate-700 shadow-2xs transition-all cursor-pointer self-start sm:self-auto"
          >
            <Edit3 className="w-3.5 h-3.5 text-slate-600" />
            <span>Manual entry</span>
          </button>
        </div>

        {/* ====================================================================
            CENTER VOICE CARD (or REVIEW CARD for final step)
        ==================================================================== */}
        {isReviewStep ? (
          /* Final Review Card */
          <div className="max-w-2xl w-full mx-auto bg-white rounded-3xl border border-slate-200/80 shadow-xs p-8 sm:p-10 text-center space-y-6">
            <div className="w-16 h-16 rounded-2xl bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 mx-auto">
              <ShieldCheck className="w-8 h-8 text-blue-600" />
            </div>

            <div>
              <h3 className="text-xl sm:text-2xl font-semibold text-slate-800">
                You're all set!
              </h3>
              <p className="text-xs sm:text-sm text-slate-500 mt-1 max-w-md mx-auto font-normal">
                All voice intake questions for {isFleet ? 'your fleet operator' : 'your hospital'} profile have been recorded. You can review and submit or edit any details manually.
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 text-left text-xs space-y-2 max-w-md mx-auto">
              <div className="flex justify-between py-1 border-b border-slate-200/60">
                <span className="text-slate-400">Profile Name:</span>
                <span className="font-bold text-slate-800">
                  {isFleet ? formData.operator_name || 'Not provided' : formData.hospital_name || 'Not provided'}
                </span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-200/60">
                <span className="text-slate-400">City & Location:</span>
                <span className="font-semibold text-slate-800">
                  {formData.city || 'N/A'} {formData.pincode ? `(${formData.pincode})` : ''}
                </span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-slate-400">Primary Contact:</span>
                <span className="font-semibold text-slate-800">
                  {formData.admin_name || formData.contacts?.[0]?.name || 'N/A'}
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <button
                type="button"
                onClick={handleSwitchToManual}
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs sm:text-sm font-semibold transition-colors cursor-pointer"
              >
                Review in manual form
              </button>

              <button
                type="button"
                onClick={handleFinalSubmit}
                className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-bold shadow-xs transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                <span>Submit application</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          /* Active Question Voice Card (Exact Mockup Layout) */
          <div className="max-w-2xl w-full mx-auto bg-white rounded-2xl sm:rounded-3xl border border-slate-200/90 shadow-sm p-6 sm:p-10 flex flex-col items-center text-center">
            {/* Top Category Badge */}
            <div className="inline-flex items-center justify-center px-4 py-1 rounded-full text-[11px] font-medium bg-slate-100 text-slate-600 mb-6 select-none border border-slate-200/50">
              {currentQ?.category || 'Details'} · Question {questionIdx + 1} of {stepQuestions.length}
            </div>

            {/* Question Heading (Simple, direct, concise) */}
            <h3 className="text-2xl sm:text-3xl font-semibold text-slate-800 mb-2 leading-tight max-w-xl">
              {currentQ?.question}
            </h3>

            {/* Subtitle Instruction */}
            <p className="text-xs sm:text-sm text-slate-500 mb-8">
              Speak your answer, then check it before continuing.
            </p>

            {!isSpeechSupported ? (
              <div className="w-full max-w-lg mb-6 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-left flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-xs sm:text-sm text-amber-900">Voice Recognition Unavailable</p>
                  <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                    Speech recognition is not supported in this browser (Web Speech API requires Google Chrome or Microsoft Edge). You can type your answers directly below or switch to{' '}
                    <button
                      type="button"
                      onClick={handleSwitchToManual}
                      className="underline font-semibold cursor-pointer text-amber-900 hover:text-amber-950"
                    >
                      Manual entry
                    </button>.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Interactive Blue Microphone Button (Pulsing ring when listening) */}
                <div className="relative mb-3 flex items-center justify-center">
                  <div
                    className={`w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 ${
                      isListening
                        ? 'bg-blue-100 ring-4 ring-blue-300/50 scale-105'
                        : 'bg-blue-100/70 hover:bg-blue-100'
                    }`}
                  >
                    {isListening && (
                      <div className="absolute inset-0 rounded-full bg-blue-400/30 animate-ping pointer-events-none" />
                    )}

                    <button
                      type="button"
                      onClick={toggleListening}
                      aria-label={isListening ? 'Stop listening' : 'Start microphone listening'}
                      title={isListening ? 'Click to stop listening' : 'Click to start speaking'}
                      className="w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center shadow-md active:scale-95 transition-all cursor-pointer relative z-10"
                    >
                      <Mic className={`w-6 h-6 text-white ${isListening ? 'animate-pulse' : ''}`} />
                    </button>
                  </div>
                </div>

                {/* Caption below Microphone */}
                <p className="text-xs text-slate-500 mb-1">
                  {isListening ? (
                    <span className="text-blue-700 font-semibold flex items-center justify-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-blue-600 animate-ping" />
                      Listening... speak now
                    </span>
                  ) : (
                    'Tap the microphone to answer'
                  )}
                </p>

                {/* Note about Chrome Web Speech API privacy */}
                <p className="text-[11px] text-slate-400 mb-6 max-w-sm">
                  Note: Chrome's Web Speech API transmits audio to Google cloud services for transcription.
                </p>
              </>
            )}

            {/* Input Field Section */}
            <div className="w-full max-w-lg text-left mb-3">
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                {currentQ?.label}
              </label>

              <input
                type="text"
                value={currentAnswer}
                onChange={(e) => setCurrentAnswer(e.target.value)}
                placeholder={currentQ?.placeholder || 'Enter your answer'}
                className="w-full px-4 py-2.5 sm:py-3 bg-white border border-slate-200 rounded-lg text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600 transition-all shadow-2xs"
              />

              {/* Optional preset pill chips for choice-based questions */}
              {currentQ?.options && currentQ.options.length > 0 && (
                <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-2.5">
                  {currentQ.options.map((opt) => {
                    const isSelected = currentQ.isMulti
                      ? currentAnswer.includes(opt)
                      : currentAnswer === opt;

                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => {
                          if (currentQ.isMulti) {
                            const parts = currentAnswer
                              ? currentAnswer.split(',').map((s) => s.trim()).filter(Boolean)
                              : [];
                            const nextParts = parts.includes(opt)
                              ? parts.filter((p) => p !== opt)
                              : [...parts, opt];
                            setCurrentAnswer(nextParts.join(', '));
                          } else {
                            setCurrentAnswer(opt);
                          }
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all cursor-pointer select-none ${
                          isSelected
                            ? 'bg-blue-50 border-blue-600 text-blue-700 font-semibold shadow-2xs'
                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Links below input: Read question aloud | Correct answer */}
            <div className="flex items-center justify-center gap-8 text-xs font-medium text-slate-600 mt-2 mb-4">
              <button
                type="button"
                onClick={handleReadAloud}
                className="hover:text-blue-600 transition-colors cursor-pointer flex items-center gap-1.5"
              >
                <span>Read question aloud</span>
              </button>

              <button
                type="button"
                onClick={() => setCurrentAnswer('')}
                className="hover:text-red-600 transition-colors cursor-pointer"
              >
                <span>Correct answer</span>
              </button>
            </div>

            {/* Sub-row adder (e.g. Add another zone, Add another ride source, Add another destination) */}
            {currentQ?.canAddSubRow && (
              <div className="mb-5">
                <button
                  type="button"
                  onClick={() => handleAddSubTableRow(currentQ.ambIndex, currentQ.subTableType)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 text-xs font-bold transition-all border border-blue-200 shadow-2xs cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5 stroke-[2.5]" />
                  <span>{currentQ.addLabel || 'Add row'}</span>
                </button>
              </div>
            )}

            {/* Table row adder (e.g. Add another area / route / contact / ambulance) */}
            {currentQ?.canAddRow && (
              <div className="mb-5">
                <button
                  type="button"
                  onClick={() => handleAddTableRow(currentQ.tableType)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 text-xs font-bold transition-all border border-blue-200 shadow-2xs cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5 stroke-[2.5]" />
                  <span>{currentQ.addLabel || 'Add another row'}</span>
                </button>
              </div>
            )}

            {/* Bottom Actions Row: [Back] | [Confirm & next] */}
            <div className="w-full max-w-lg flex items-center justify-between">
              <button
                type="button"
                onClick={handleBack}
                disabled={isFirst}
                className="px-5 py-2 sm:py-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs sm:text-sm font-semibold transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed shadow-2xs"
              >
                Back
              </button>

              <button
                type="button"
                onClick={handleConfirmNext}
                className="px-6 py-2 sm:py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-bold shadow-sm transition-all cursor-pointer flex items-center gap-2"
              >
                <span>
                  {questionIdx === stepQuestions.length - 1 && currentStep === stepsList.length - 1
                    ? 'Confirm & review'
                    : 'Confirm & next'}
                </span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Skip for now centered below */}
            <div className="mt-4">
              <button
                type="button"
                onClick={handleSkip}
                className="text-xs text-slate-400 hover:text-slate-600 transition-colors cursor-pointer font-medium"
              >
                Skip for now
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Minimal Footer */}
      <footer className="py-5 text-center text-xs text-slate-400 border-t border-slate-200/80 bg-white">
        © {new Date().getFullYear()} RISA Smart Ambulance Platform. All rights reserved.
      </footer>
    </div>
  );
};

export default HospitalOnboardingVoicePage;
